import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export interface Crumb {
  label: string;
  /** Where it goes (the last crumb — this page — has none). */
  href?: string;
}

/**
 * #153 the same way back and the same breadcrumbs on every full page (and under the project shell). Back returns to
 * the previous screen in Fyr; opened straight from a link (no Fyr history), it goes up a level — the crumb above.
 */
export function PageCrumbs({ crumbs, className }: { crumbs: Crumb[]; className?: string }) {
  const { t } = useTranslation();
  return (
    <nav
      className={`breadcrumbs page-crumbs${className ? ` ${className}` : ''}`}
      aria-label={t('tree.breadcrumbs')}
    >
      <BackButton fallback={[...crumbs].reverse().find((c) => c.href)?.href ?? '/'} />
      {crumbs.map((c, i) => (
        <span key={`${i}:${c.label}`}>
          {i > 0 && <span className="sep">›</span>}
          {c.href && i < crumbs.length - 1 ? (
            <Link to={c.href}>{c.label}</Link>
          ) : (
            <span
              className={i === crumbs.length - 1 ? 'current' : ''}
              aria-current={i === crumbs.length - 1 ? 'page' : undefined}
            >
              {c.label}
            </span>
          )}
        </span>
      ))}
    </nav>
  );
}

export function BackButton({ fallback }: { fallback: string }) {
  const { t } = useTranslation();
  const nav = useNavigate();
  // React Router numbers the entries it pushed: above 0 = there's a Fyr screen to go back to
  const canGoBack = ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;
  return (
    <button
      type="button"
      className="link-btn back-btn"
      onClick={() => (canGoBack ? nav(-1) : nav(fallback))}
      title={t('nav.back')}
    >
      <span aria-hidden>←</span> {t('nav.back')}
    </button>
  );
}
